import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI, gamificationAPI, resolveFileUrl } from '../services/api';
import type { MyPointsData } from '../types';
import { useAuth } from '../context/AuthContext';

const PURPLE = '#200854';
const ORANGE = '#FF6600';

interface ProfileData {
  name: string;
  surname: string;
  phone: string;
  riderLevel: string;
  averagePace: string;
  groupRidingExperience: string;
  bikeType: string;
  bike: string;
  dressSize: string;
}

const NajSportProfile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [user, setUser] = useState<any>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileData>({
    name: '', surname: '', phone: '', riderLevel: '', averagePace: '',
    groupRidingExperience: '', bikeType: '', bike: '', dressSize: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [stats, setStats] = useState({ totalPoints: 0, rideCount: 0, currentStreak: 0 });
  const [pointsData, setPointsData] = useState<MyPointsData | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [isPasswordUser, setIsPasswordUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [gamTab, setGamTab] = useState<'rewards' | 'howto'>('rewards');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem('najsportUser');
    if (!stored) {
      navigate('/');
      return;
    }
    const u = JSON.parse(stored);
    setUser(u);
    setPhotoUrl(u.photoUrl || null);

    authAPI.getMe().then((me: any) => {
      setForm({
        name: me.name || '',
        surname: me.surname || '',
        phone: me.phone || '',
        riderLevel: me.riderLevel || '',
        averagePace: me.averagePace || '',
        groupRidingExperience: me.groupRidingExperience === true ? 'yes' : me.groupRidingExperience === false ? 'no' : '',
        bikeType: me.bikeType || '',
        bike: me.bike || '',
        dressSize: me.dressSize || '',
      });
      if (me.photoUrl) setPhotoUrl(me.photoUrl);
      setIsPasswordUser(me.role === 'ADMIN' || me.role === 'POWER_ADMIN' || me.role === 'TRAINER');
      setStats({
        totalPoints: me.totalPoints ?? 0,
        rideCount: me.rideCount ?? 0,
        currentStreak: me.currentStreak ?? 0,
      });
      // Load gamification data
      gamificationAPI.getMyPoints().then((data: MyPointsData) => setPointsData(data)).catch(() => {});
    }).catch((err) => {
      console.error('Failed to load profile:', err);
      setMessage(t('najsport.errorSaving'));
    }).finally(() => setProfileLoading(false));
  }, [navigate]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const result = await authAPI.uploadProfilePhoto(file);
      // Append cache-buster to force browser to reload the new image
      const bustUrl = result.photoUrl + '?v=' + Date.now();
      setPhotoUrl(bustUrl);
      // Update localStorage
      const stored = localStorage.getItem('najsportUser');
      if (stored) {
        const u = JSON.parse(stored);
        u.photoUrl = result.photoUrl;
        localStorage.setItem('najsportUser', JSON.stringify(u));
      }
    } catch {
      setMessage(t('najsport.errorPhotoUpload'));
    }
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.surname.trim() || !form.riderLevel || !form.averagePace || !form.groupRidingExperience) {
      setMessage(t('najsport.fillRequired'));
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const updated = await authAPI.updateMe({
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: form.phone.trim() || undefined,
        riderLevel: form.riderLevel,
        averagePace: form.averagePace,
        groupRidingExperience: form.groupRidingExperience === 'yes',
        bikeType: form.bikeType || undefined,
        bike: form.bike.trim() || undefined,
        dressSize: form.dressSize || undefined,
      });
      const stored = localStorage.getItem('najsportUser');
      if (stored) {
        const u = JSON.parse(stored);
        u.name = updated.name;
        u.surname = updated.surname;
        u.onboardingCompleted = true;
        localStorage.setItem('najsportUser', JSON.stringify(u));
      }
      setMessage(t('najsport.profileSaved'));
    } catch {
      setMessage(t('najsport.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage(t('najsport.fillRequired'));
      return;
    }
    if (newPassword.length < 6) {
      setPasswordMessage(t('najsport.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage(t('najsport.passwordsNoMatch'));
      return;
    }
    setSavingPassword(true);
    setPasswordMessage('');
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordMessage(t('najsport.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err.response?.data?.error;
      if (msg === 'Current password is incorrect') {
        setPasswordMessage(t('najsport.wrongPassword'));
      } else {
        setPasswordMessage(t('najsport.errorSaving'));
      }
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) return null;

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="shadow-md" style={{ backgroundColor: PURPLE }}>
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/najsport-logo.png" alt="NajSport" className="h-8 w-auto" />
              <h1 className="text-lg font-bold text-white">Profil</h1>
            </div>
          </div>
        </header>
        <main className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-5 animate-pulse">
            <div className="flex flex-col items-center gap-2">
              <div className="w-24 h-24 rounded-full bg-gray-200" />
              <div className="h-3 bg-gray-200 rounded w-32" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl p-3 h-16" />
              ))}
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
                <div className="h-10 bg-gray-200 rounded w-full" />
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500 text-sm";
  const labelClass = "block text-sm font-medium mb-1";
  const initials = ((form.name?.[0] || '') + (form.surname?.[0] || '')).toUpperCase() || user.email?.[0]?.toUpperCase() || '?';

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="shadow-md" style={{ backgroundColor: PURPLE }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/najsport-logo.png" alt="NajSport" className="h-8 w-auto" />
            <h1 className="text-lg font-bold text-white">{t('najsport.profile')}</h1>
          </div>
          <button
            onClick={() => navigate('/events')}
            className="flex items-center gap-1 text-sm text-white hover:text-orange-300 transition-colors font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            {t('najsport.rides')}
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-5">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-2">
            <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="relative group"
            >
              {photoUrl ? (
                <img
                  src={resolveFileUrl(photoUrl)}
                  alt="Avatar"
                  className="w-24 h-24 rounded-full object-cover border-4"
                  style={{ borderColor: PURPLE }}
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold border-4"
                  style={{ backgroundColor: PURPLE, borderColor: PURPLE }}
                >
                  {initials}
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-xs font-medium">{uploadingPhoto ? t('najsport.uploading') : t('najsport.change')}</span>
              </div>
            </button>
            <div className="text-sm text-gray-500">{user.email}</div>
          </div>

          {/* Language Switcher */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.language')}</label>
            <div className="flex gap-2">
              {[
                { code: 'sk', label: 'Slovenčina', flag: '🇸🇰' },
                { code: 'en', label: 'English', flag: '🇬🇧' },
                { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    i18n.changeLanguage(lang.code);
                    localStorage.setItem('tri2fly-lang', lang.code);
                    authAPI.updateMe({ preferredLanguage: lang.code }).catch(() => {});
                  }}
                  className={`flex-1 py-2.5 px-2 rounded-lg text-sm font-medium border-2 transition-all flex items-center justify-center gap-1.5 ${
                    i18n.language === lang.code
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={i18n.language === lang.code ? { backgroundColor: PURPLE } : {}}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Level Badge & Progress */}
          {pointsData && (
            <div className="rounded-xl p-4" style={{ background: `linear-gradient(135deg, ${PURPLE}, #4a1d96)` }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-orange-400 text-xs font-bold uppercase tracking-wider">Level {pointsData.level.level}</div>
                  <div className="text-white text-xl font-bold">{pointsData.level.name}</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white">{pointsData.totalPoints}</div>
                  <div className="text-purple-200 text-xs">{t('najsport.points')}</div>
                </div>
              </div>
              {pointsData.level.nextLevel && (
                <div>
                  <div className="flex justify-between text-xs text-purple-200 mb-1">
                    <span>{pointsData.level.name}</span>
                    <span>{pointsData.level.nextLevel.name} ({pointsData.level.nextLevel.pointsNeeded} {t('najsport.points').toLowerCase()})</span>
                  </div>
                  <div className="w-full bg-purple-900/50 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        backgroundColor: ORANGE,
                        width: `${Math.min(100, ((pointsData.totalPoints - pointsData.level.minPoints) / (pointsData.level.nextLevel.pointsNeeded - pointsData.level.minPoints)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-purple-50 rounded-xl p-3">
              <div className="text-2xl font-bold" style={{ color: PURPLE }}>{stats.totalPoints}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{t('najsport.points')}</div>
            </div>
            <div className="bg-purple-50 rounded-xl p-3">
              <div className="text-2xl font-bold" style={{ color: PURPLE }}>{stats.rideCount}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{t('najsport.ridesCount')}</div>
            </div>
            <div className="bg-purple-50 rounded-xl p-3">
              <div className="text-2xl font-bold" style={{ color: PURPLE }}>{stats.currentStreak}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{t('najsport.streak')}</div>
            </div>
          </div>

          {/* Points Info — Rewards & How to Earn */}
          {pointsData && (
            <div className="bg-gray-50 rounded-2xl overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                {(['rewards', 'howto'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setGamTab(tab)}
                    className={`flex-1 py-2.5 text-xs font-bold transition-colors ${
                      gamTab === tab
                        ? 'text-white'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    style={gamTab === tab ? { backgroundColor: PURPLE } : {}}
                  >
                    {tab === 'rewards' ? t('najsport.rewards') : t('najsport.howToEarn')}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {gamTab === 'rewards' ? (
                  <div className="space-y-2">
                    {[
                      { pts: 50, label: t('najsport.reward5pct'), icon: '🏷️' },
                      { pts: 150, label: t('najsport.reward10pct'), icon: '💰' },
                      { pts: 200, label: t('najsport.rewardService'), icon: '🔧' },
                      { pts: 300, label: t('najsport.rewardBike'), icon: '🚲' },
                    ].map((r) => {
                      const unlocked = pointsData.totalPoints >= r.pts;
                      const progress = Math.min(100, (pointsData.totalPoints / r.pts) * 100);
                      return (
                        <div
                          key={r.pts}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 ${
                            unlocked ? 'border-green-300 bg-green-50' : 'border-gray-100 bg-white'
                          }`}
                        >
                          <span className="text-xl">{r.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className={`text-sm font-medium ${unlocked ? 'text-green-700' : 'text-gray-600'}`}>{r.label}</span>
                              <span className={`text-xs font-bold ${unlocked ? 'text-green-600' : 'text-gray-400'}`}>{r.pts} b.</span>
                            </div>
                            {!unlocked && (
                              <div className="w-full bg-gray-200 rounded-full h-1 mt-1.5">
                                <div className="h-1 rounded-full" style={{ width: `${progress}%`, backgroundColor: ORANGE }} />
                              </div>
                            )}
                          </div>
                          {unlocked && <span className="text-green-500 text-lg">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Attendance */}
                    <div className="bg-white rounded-xl p-3 border border-gray-100">
                      <div className="text-xs font-bold mb-2" style={{ color: PURPLE }}>🚴 {t('najsport.earnAttendance')}</div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>{t('najsport.earnPerRide')}</span>
                        <span className="font-bold" style={{ color: ORANGE }}>+10</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                        <span>{t('najsport.earnSpecialEvent')}</span>
                        <span className="font-bold" style={{ color: ORANGE }}>+10</span>
                      </div>
                    </div>
                    {/* Competition */}
                    <div className="bg-white rounded-xl p-3 border border-gray-100">
                      <div className="text-xs font-bold mb-2" style={{ color: PURPLE }}>🏆 {t('najsport.earnCompetition')}</div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>🥇 {t('najsport.earnPlace1')}</span>
                        <span className="font-bold" style={{ color: ORANGE }}>+30</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                        <span>🥈 {t('najsport.earnPlace2')}</span>
                        <span className="font-bold" style={{ color: ORANGE }}>+20</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                        <span>🥉 {t('najsport.earnPlace3')}</span>
                        <span className="font-bold" style={{ color: ORANGE }}>+10</span>
                      </div>
                    </div>
                    {/* Streak */}
                    <div className="bg-white rounded-xl p-3 border border-gray-100">
                      <div className="text-xs font-bold mb-2" style={{ color: PURPLE }}>🔥 {t('najsport.earnStreak')}</div>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>{t('najsport.earnStreak3')}</span>
                        <span className="font-bold" style={{ color: ORANGE }}>+10</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                        <span>{t('najsport.earnStreak6')}</span>
                        <span className="font-bold" style={{ color: ORANGE }}>+30</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Meno */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.profileName')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputClass}
              placeholder={t('najsport.yourName')}
            />
          </div>

          {/* Priezvisko */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.profileSurname')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.surname}
              onChange={(e) => setForm({ ...form, surname: e.target.value })}
              className={inputClass}
              placeholder={t('najsport.yourSurname')}
            />
          </div>

          {/* Telefón */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.profilePhone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputClass}
              placeholder="+421 9XX XXX XXX"
            />
          </div>

          {/* Úroveň jazdca */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.riderLevel')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'beginner', label: t('najsport.beginner') },
                { value: 'intermediate', label: t('najsport.intermediate') },
                { value: 'advanced', label: t('najsport.advanced') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, riderLevel: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-xs font-medium border-2 transition-all ${
                    form.riderLevel === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.riderLevel === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Priemerné tempo */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.averagePace')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'chill', label: t('najsport.chill') },
                { value: 'sport', label: t('najsport.sport') },
                { value: 'performance', label: t('najsport.performance') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, averagePace: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-xs font-medium border-2 transition-all ${
                    form.averagePace === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.averagePace === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Skúsenosti s jazdou v skupine */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.groupExperience')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'yes', label: t('najsport.yes') },
                { value: 'no', label: t('najsport.no') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, groupRidingExperience: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.groupRidingExperience === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.groupRidingExperience === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Typ bicykla */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.bikeType')}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'road', label: t('najsport.road') },
                { value: 'gravel', label: t('najsport.gravel') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, bikeType: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.bikeType === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.bikeType === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Bicykel */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.bikeModel')}</label>
            <input
              type="text"
              value={form.bike}
              onChange={(e) => setForm({ ...form, bike: e.target.value })}
              className={inputClass}
              placeholder={t('najsport.bikeModelPlaceholder')}
            />
          </div>

          {/* Veľkosť dresu */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.jerseySize')}</label>
            <select
              value={form.dressSize}
              onChange={(e) => setForm({ ...form, dressSize: e.target.value })}
              className={inputClass}
            >
              <option value="">{t('najsport.selectSize')}</option>
              <option value="XS">XS</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
              <option value="XXL">XXL</option>
            </select>
          </div>

          {/* Change Password (admin/trainer only) */}
          {isPasswordUser && (
            <div className="border-t pt-5 space-y-3">
              <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.changePassword')}</label>
              <div>
                <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.currentPassword')}</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.confirmNewPassword')}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                />
              </div>
              {passwordMessage && (
                <div className={`text-sm text-center py-2 rounded-lg ${passwordMessage === t('najsport.passwordChanged') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {passwordMessage}
                </div>
              )}
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={savingPassword}
                className="w-full py-2.5 rounded-lg text-white font-bold text-sm disabled:opacity-50 transition-all"
                style={{ backgroundColor: PURPLE }}
              >
                {savingPassword ? t('najsport.saving') : t('najsport.changePassword')}
              </button>
            </div>
          )}

          {message && (
            <div className={`text-sm text-center py-2 rounded-lg ${message === t('najsport.profileSaved') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {message}
            </div>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 rounded-lg text-white font-bold text-sm disabled:opacity-50 transition-all hover:shadow-lg"
            style={{ backgroundColor: ORANGE }}
          >
            {saving ? t('najsport.saving') : t('najsport.saveProfile')}
          </button>

          {/* GDPR Section */}
          <div className="border-t pt-5 space-y-3">
            <h3 className="text-sm font-bold" style={{ color: PURPLE }}>
              {t('najsport.gdprTitle')}
            </h3>
            <button
              type="button"
              onClick={() => navigate('/privacy-policy')}
              className="w-full text-left text-sm text-gray-600 hover:text-gray-800 underline transition-colors"
            >
              {t('najsport.gdprPrivacyPolicy')}
            </button>
            <button
              type="button"
              onClick={async () => {
                setExporting(true);
                try {
                  const data = await authAPI.exportMyData();
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `najsport-my-data-${new Date().toISOString().slice(0, 10)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  setMessage(t('najsport.errorSaving'));
                } finally {
                  setExporting(false);
                }
              }}
              disabled={exporting}
              className="w-full py-2.5 rounded-lg text-sm font-medium border-2 border-gray-200 text-gray-700 hover:border-gray-300 transition-all disabled:opacity-50"
            >
              {exporting ? '...' : t('najsport.gdprExportData')}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full py-2.5 rounded-lg text-sm font-medium border-2 border-red-200 text-red-600 hover:bg-red-50 transition-all"
            >
              {t('najsport.gdprDeleteAccount')}
            </button>
          </div>
        </div>

        {/* Delete Account Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4 animate-fadeIn">
              <h3 className="text-lg font-bold text-red-600">{t('najsport.gdprDeleteTitle')}</h3>
              <p className="text-sm text-gray-600">{t('najsport.gdprDeleteWarning')}</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium border-2 border-gray-200 text-gray-700 hover:border-gray-300 transition-all"
                >
                  {t('najsport.cancel')}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await authAPI.deleteMyAccount();
                      logout();
                      navigate('/');
                    } catch {
                      setMessage(t('najsport.errorSaving'));
                      setShowDeleteConfirm(false);
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting}
                  className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition-all disabled:opacity-50"
                >
                  {deleting ? '...' : t('najsport.gdprDeleteConfirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default NajSportProfile;
